import axios from "axios";
import { store } from "Store/mainStore";
import { showModal } from "Store/auth";
import { fetchPriceListPayload } from "../types";

type DatamartApiResult = {
  statusCode?: number;
  statusText?: string;
  status?: boolean | number;
  message?: string;
  data?: any;
  total_count?: number;
  requestId?: string;
};

type DatamartApiRequest = {
  method: string;
  url: string;
  data?: unknown;
  customHeaders?: Record<string, string>;
};

const parseProfitabilityResult = (res: any) => {
  const body = res?.data ?? {};
  const statusInfo = body.status;
  const result =
    body.result ??
    (body.data && typeof body.data === 'object'
      ? body.data.result ?? body.data
      : null) ??
    {};
  const gridSource =
    Array.isArray(result.header) || Array.isArray(result.rows)
      ? result
      : result.data && typeof result.data === 'object'
        ? result.data
        : result;

  // Legacy datamart envelopes report failures as HTTP 200 with `status: false`.
  const isLegacyFailure = statusInfo === false;

  return {
    status:
      !isLegacyFailure && (statusInfo?.code === 200 || res.status === 200),
    header: gridSource.header || [],
    rows: gridSource.rows || [],
    payload: result.payload ?? gridSource.payload ?? null,
    itemSource: result.itemSource ?? gridSource.itemSource ?? null,
    metadata: result.metadata ?? gridSource.metadata ?? null,
    hasLineItems:
      result.hasLineItems === true || gridSource.hasLineItems === true,
    message: statusInfo?.message || body.errorMessage || '',
  };
};

const profitabilityErrorResult = (error: any) => ({
  status: false,
  header: [],
  rows: [],
  payload: null,
  itemSource: null,
  metadata: null,
  hasLineItems: false,
  message:
    error?.response?.data?.errorMessage ||
    error?.response?.data?.status?.message ||
    error?.message ||
    'Unknown error',
});

/** Shown inline on the Analyse page, so the global error popup is skipped for it. */
export const PROFITABILITY_PRICELIST_ERROR =
  'Pricelist error: No valid pricelist detected. Please contact commercial management.';

const isPricelistError = (message?: string): boolean =>
  String(message || '').trim() === PROFITABILITY_PRICELIST_ERROR;

/** Same "Something went wrong" modal the global OOT interceptor raises. */
const showGlobalErrorDialog = (message: string, stacktrace?: string) => {
  store.dispatch(
    showModal({
      msg: message,
      variant: 'ERROR',
      title: 'Something went wrong',
      show: true,
      panel: { title: 'More details', msg: stacktrace || '' },
    }),
  );
};

const EXCEL_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const getResponseHeader = (headers: any, name: string): string => {
  if (!headers) return '';

  if (typeof headers.get === 'function') {
    return String(headers.get(name) || headers.get(name.toLowerCase()) || '');
  }

  const matchKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === name.toLowerCase(),
  );
  const value = matchKey ? headers[matchKey] : undefined;
  if (Array.isArray(value)) return String(value[0] || '');
  return String(value || '');
};

const parseContentDispositionFilename = (header?: string): string => {
  if (!header) return '';

  const utf8Match = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (utf8Match?.[1]) {
    const encoded = utf8Match[1].trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }

  const filenameMatch = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].replace(/['"]/g, '').trim();
  }

  return '';
};

const sanitizeExportFileNamePart = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[<>:"/\\|?*]/g, '');

/** `{opp_id}_{opp_name}_{offer_id}_{yyyy-mm-dd}.xlsx` when Content-Disposition is not readable. */
export const buildProfitabilityExportFileName = ({
  oppId = '',
  oppName = '',
  offerId,
  date = new Date(),
}: {
  oppId?: string;
  oppName?: string;
  offerId: string;
  date?: Date;
}): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const stem = [oppId, oppName, offerId]
    .map(sanitizeExportFileNamePart)
    .filter(Boolean)
    .join('_');

  return `${stem}_${yyyy}-${mm}-${dd}.xlsx`;
};

const messageFromExportPayload = (payload: unknown, fallback: string): string => {
  if (!payload || typeof payload !== 'object') return fallback;
  const data = payload as Record<string, any>;
  return (
    data?.status?.message ||
    data?.errorMessage ||
    data?.message ||
    fallback
  );
};

const readBlobAsText = async (blob: Blob): Promise<string> => {
  if (typeof Response !== 'undefined') {
    try {
      const text = await new Response(blob).text();
      if (text) return text;
    } catch {
      // Fall through to Blob.text / FileReader.
    }
  }

  if (typeof blob.text === 'function') {
    try {
      const text = await blob.text();
      if (text) return text;
    } catch {
      // Fall through to FileReader for jsdom / older Blob implementations.
    }
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

const parseBlobErrorMessage = async (blob: Blob, fallback: string): Promise<string> => {
  try {
    const text = await readBlobAsText(blob);
    return messageFromExportPayload(JSON.parse(text), fallback);
  } catch {
    return fallback;
  }
};

const unwrapCockpitDatamartPayload = (payload: unknown) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload)) {
    return { rows: payload };
  }
  return {};
};

/** Escape literals before they are interpolated into datamart SQL filter strings. */
const escapeDatamartFilterLiteral = (value: string): string =>
  String(value).replace(/\\/g, "\\\\").replace(/'/g, "''").replace(/"/g, '\\"');

class DatamartServices {

  getState() {
    // Import your Redux store and selector
    const reduxtStore = store.getState();
    // Placeholder for demonstration:
    const email = reduxtStore.authSlice?.login?.mail || ""; // Replace with actual Redux state value
    const userId = reduxtStore.authSlice?.login?.id || ""; // Get user ID from Redux state
    return { userId, email };
  }

  /**
   * Extracts a user-facing message from an axios error.
   * Prefers the datamart `status.message` / `status.description` body, then the
   * legacy `errorMessage`, then the axios error message.
   */
  private getErrorMessageFromError(error: any, fallback = 'Unknown error'): string {
    return (
      error?.response?.data?.status?.message ||
      error?.response?.data?.status?.description ||
      error?.response?.data?.errorMessage ||
      error?.message ||
      fallback
    );
  }

  /**
   * Fetches the price list from the API.
   * @param params The parameters for fetching the price list.
   * @returns The API response or an error object.
   */
  fetchPriceList = async (params: fetchPriceListPayload) => {
    const { offset, limit, sort, search, priceListId, solutionType, skipOptions = false } = params;
    // Construct the API request details
    // Prepare the API endpoint and parameters
    const searchParams = new URLSearchParams();

    if (solutionType) searchParams.append('solutionType', String(solutionType));
    if (priceListId) searchParams.append('priceListId', String(priceListId));
    if (limit) searchParams.append('limit', String(limit));
    if (offset) searchParams.append('offset', String(offset));
    if (sort) searchParams.append('sort', String(sort));
    if (search) searchParams.append('search', String(search));
    // Attach skipOptions parameter
    searchParams.append('skipOptions', String(skipOptions));
    // The API endpoint is relative to the base URL defined in the .env file
    const priceListParam: DatamartApiRequest = {
      method: 'get',
      url: `/v2/pricing/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
      data: undefined,
      customHeaders: {
        'getcount': 'true',
      }
    };
    const res = await this.genericDatamartAPI(priceListParam);
    //Handle the response error beloe before returning
    // --- Need to handle the error response here ---
    // Return the response in a consistent format
    return { status: res.status, data: res?.data || [], total_count: res?.total_count ?? 0, message: res?.message || '' };
  }

  /**  Saves discount data to the pricing service.
  * @param {Object} pricingData - The parameters for saving discount data.
  *   This should include all necessary fields required by the pricing service.
  * @return {Promise<Object>} A promise that resolves to the API response or an error object.
  * */
  saveDiscountData = async (pricingData: any) => {
    // Construct the API request details
    const saveDiscountDataParam: DatamartApiRequest = {
      method: 'post',
      url: `/v2/pricing`,
      data: pricingData,
    };
    try {
      // Make the API request using the genericDatamartAPI function
      const res = await this.genericDatamartAPI(saveDiscountDataParam);
      // Check if the response is valid and contains data
      if (res.status) {
        return { status: true, data: res.data || [], message: res?.message || '' };
      } else {
        return { status: false, message: res?.message || 'Failed to save discount data' };
      }
    } catch (error: any) {
      console.error("Error in saveDiscountData:", error);
      return { status: false, message: error?.message || 'Unknown error occurred' };
    }
  }

  /**  * Deletes a price list by its ID.
  * @param {number} priceListId - The ID of the price list to delete.
  * @return {Promise<Object>} A promise that resolves to the API response or an error object.
  * */
  // Note: The priceListId is expected to be a number, and it should be passed as an argument to this function.
  // If the priceListId is not provided, it returns an error message.
  deletePriceList = async (priceListId: number) => {
    if (!priceListId) {
      return { status: false, message: 'Price List ID is required for deletion.' };
    }
    // Construct the API request details
    const deletePriceListParam: DatamartApiRequest = {
      method: 'delete',
      url: `/v2/pricing/${priceListId}`,
      data: undefined,
      customHeaders: {}
    };
    const res = await this.genericDatamartAPI(deletePriceListParam);
    return { status: true, message: res?.message || '', data: res?.data || [] };
  }

  /**
   * Fetches discount options based on search text and field.
   * @param params - The parameters for fetching discount options.
   * @returns 
   */

  fetchDiscountOptions = async (params: { search?: string; field?: string }) => {
    const { search, field } = params;
    // Construct the API request details
    // Prepare the API endpoint and parameters
    const searchParams = new URLSearchParams();
    if (search) searchParams.append('search', String(search));
    if (field) searchParams.append('field', String(field));
    searchParams.append('offset', '0');
    searchParams.append('limit', '30');
    // The API endpoint is relative to the base URL defined in the .env file
    const priceListParam: DatamartApiRequest = {
      method: 'get',
      url: `/v2/pricing/options/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
      data: undefined,
    };
    const res = await this.genericDatamartAPI(priceListParam);
    // Return the response in a consistent format
    return { status: res.status, data: res?.data || [], total_count: res?.total_count ?? 0 };
  }


  /**
   * Fetches a list of accounts from the Datamart service.
   * @param {Array<string>} filters - Optional filters to apply to the account search.
   * @param {number} offset - The offset for pagination.
   * @returns {Promise<Array<Object>>} A promise that resolves to the list of accounts.
   */
  getAccountList = async (filters: string | string[] | undefined = undefined, offset = 0) => {
    // Construct the API request details
    const filterList = Array.isArray(filters)
      ? filters
      : filters
        ? [filters]
        : [];
    const bodyParams = {
      "properties": [
        "accountnumber",
        "name",
      ],
      filter: filterList.length > 0 ? [`account.accountnumber is not null AND (${filterList.join(" ")})`] : ["account.accountnumber is not null"],
      "supplier_id": [],
      "limit": 30,
      "offset": offset,
    }

    // Construct the API request details
    const accountParam: DatamartApiRequest = {
      method: 'post',
      url: `/v1/account`,
      data: bodyParams,
      customHeaders: {
        operation: "read",
        requested_info: 'sell_to_party'
      }
    };
    // This api should happen recursively to get all the accounts
    // If you want to fetch all accounts, you might need to implement pagination logic here
    const outputResponse: any[] = [];
    // do {
    const res = await this.genericDatamartAPI(accountParam);
    // Check if the response is valid and contains data
    if (res.data && res.data.length > 0) {
      outputResponse.push(...res.data);
    }
    return outputResponse;
  }

  /**
  * A generic function to make API calls (GET, POST, PUT, DELETE) to the Datamart service.
  * Handles errors and displays appropriate error dialogs if the request fails.
  * 
  * @param {Object} param - The API request details.
  * @param {string} param.method - The HTTP method (e.g., GET, POST, PUT, DELETE).
  * @param {string} param.url - The endpoint URL (relative to the Datamart base URL).
  * @param {Object} param.data - The request payload.
  * @returns {Promise<Object>} A promise that resolves to the API response or an error object.
  */
  async genericDatamartAPI({
    method,
    url,
    data,
    customHeaders = {},
  }: DatamartApiRequest): Promise<DatamartApiResult> {
    try {
      // Get the userId from the Redux store
      const { userId } = this.getState();
      // Prepare the API request configuration
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        "x-user-id": userId,
      };
      // Handle other headers if provided
      if (customHeaders && typeof customHeaders === 'object') {
        Object.keys(customHeaders).forEach((key) => {
          const value = customHeaders[key];
          if (value && value.trim().length > 0) {
            headers[key] = value;
          }
        });
      }
      const config = {
        method: method,
        url: `${process.env.REACT_APP_DATAMART}${url}`,
        data: data,
        headers,
      };
      // Make the API request using axios
      const res = await axios(config);
      // prepare the response format
      const response: DatamartApiResult = {
        statusCode: res.status,
        statusText: res.statusText,
        status: res.data?.status,
        message: res.data?.message || '',
        data: res?.data?.data || [],
        total_count: res?.data?.total_count || 0,
        requestId: res?.data?.requestId || '',
      }
      return response;
    } catch (err: unknown) {
      console.error("Error in genericDatamartAPI:", err);
      const axiosError = err as { response?: { status?: number } };
      // showResponseDialog(this.redX.dispatch, err?.response?.status, { msg: err.code, panel: { title: 'Failed to connect server', msg: errMesg } });
      return {
        data: [],
        statusCode: axiosError?.response?.status,
        status: false,
        message: this.getErrorMessageFromError(err, ''),
      };
    }
  }

  validateUniqueIds = async (data: unknown) => {
    const priceListParam: DatamartApiRequest = {
      method: 'post',
      url: `/v2/pricing/validate-uniqueids`,
      data
    };
    const res = await this.genericDatamartAPI(priceListParam);
    return { status: res.status, data: res?.data || [] };
  }

  /**
   * Fetches a single offer by offer_id for Analyse profitability.
   * `is_submitted` is not requested in properties (datamart rejects it) but is
   * still mapped from the response, same as the offer dashboard.
   */
  fetchOfferForAnalysis = async (offerId: string) => {
    if (!offerId) {
      return { status: false, data: null as null, message: 'Offer id is required' };
    }

    const properties = [
      'offer_id',
      'temp_offer_id',
      'case_name',
      'opportunity_id',
      'currency',
      'sell_to_party_accountnumber',
      'sell_to_party_three_star_accountnumber',
      'lead_product',
      'solution',
      'shipping_country',
      'source',
      'commercial_model',
      'owner_email',
      'status',
      'ui_json',
    ];

    const res = await this.genericDatamartAPI({
      method: 'post',
      url: '/v2/offer',
      data: {
        offer_id: [],
        properties,
        limit: 1,
        offset: 0,
        filter: `offer_id = '${escapeDatamartFilterLiteral(offerId)}'`,
      },
      customHeaders: {
        operation: 'read',
      },
    });
    const raw = Array.isArray(res?.data) ? res.data[0] : null;

    if (!res?.status || !raw) {
      return {
        status: false,
        data: null as null,
        message: res?.message || 'Offer not found',
      };
    }

    let uiJson: any = null;
    try {
      uiJson = raw.ui_json
        ? typeof raw.ui_json === 'string'
          ? JSON.parse(raw.ui_json)
          : raw.ui_json
        : null;
    } catch (e) {
      console.error('Failed to parse offer ui_json:', e);
      uiJson = null;
    }

    return {
      status: true,
      data: {
        offer_id: raw.offer_id,
        temp_offer_id: raw.temp_offer_id,
        caseName: raw.case_name,
        case_name: raw.case_name,
        sCRMID: raw.opportunity_id,
        caseCode: raw.opportunity_id,
        currency: raw.currency,
        sell_to_party_accountnumber: raw.sell_to_party_accountnumber,
        sell_to_party_three_star_accountnumber:
          raw.sell_to_party_three_star_accountnumber,
        lead_product: raw.lead_product,
        solution: raw.solution,
        shipping_country: raw.shipping_country,
        source: raw.source || 'cui',
        commercial_model: raw.commercial_model,
        owner: raw.owner_email,
        offerStatus: raw.status,
        status: raw.status,
        is_submitted: raw.is_submitted,
        ui_json: uiJson,
        solutionCategory: uiJson?.config_stepper?.data?.solutionCategory,
        solutionType: uiJson?.config_stepper?.data?.solutionType,
      },
      message: res?.message || '',
    };
  };

  /**
   * POST /v2/pricing/profitability with a normalized `{ action, data }` body.
   */
  postProfitabilityUI = async (
    action: string,
    data: Record<string, unknown> = {},
  ) => {
    return this.fetchProfitabilityUI({
      action,
      data: { ...data, action },
    });
  };

  /**
   * Fetches the profitability breakdown grid (dynamic header + rows).
   * The response is nested under `result` (header/rows) so we bypass the
   * generic wrapper (which only surfaces `data`) and read the raw payload.
   * @param {Object} postData - The profitability calc request body.
   * @returns {Promise<{ status: boolean; header: any[]; rows: any[]; message: string }>}
   */
  fetchProfitabilityUI = async (postData: any) => {
    const { userId } = this.getState();
    try {
      const res = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_DATAMART}/v2/pricing/profitability`,
        data: postData,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        // Popup is raised below for every error except the pricelist one.
        _skipGlobalErrorDialog: true,
      } as any);
      const result = parseProfitabilityResult(res);
      if (!result.status && !isPricelistError(result.message)) {
        showGlobalErrorDialog(result.message, res?.data?.stacktrace);
      }
      return result;
    } catch (error: any) {
      console.error('Error in fetchProfitabilityUI:', error);
      const result = profitabilityErrorResult(error);
      if (!isPricelistError(result.message)) {
        showGlobalErrorDialog(
          result.message,
          error?.response?.data?.stacktrace,
        );
      }
      return result;
    }
  };

  /**
   * POST /v2/pricing/applyCockpit — same body shape as profitability with discount_cockpit.
   */
  applyCockpit = async (postData: {
    action: string;
    data: Record<string, unknown>;
    discount_cockpit: Record<string, string>[];
  }) => {
    const { userId } = this.getState();
    const { action, data, discount_cockpit } = postData;
    try {
      const res = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_DATAMART}/v2/pricing/cockpit/applyCockpit`,
        data: {
          action,
          data: { ...data, action },
          discount_cockpit,
        },
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });
      return parseProfitabilityResult(res);
    } catch (error: any) {
      console.error('Error in applyCockpit:', error);
      return profitabilityErrorResult(error);
    }
  };

  /**
   * GET /v2/pricing/getCockpit — loads cockpit reserves and pricing context.
   * @param offerId Offer / case id
   */
  fetchCockpit = async (offerId: string) => {
    if (!offerId) {
      return { status: false, data: [], message: 'Offer id is required' };
    }

    const searchParams = new URLSearchParams();
    searchParams.append('offerId', offerId);

    const res = await this.genericDatamartAPI({
      method: 'get',
      url: `/v2/pricing/cockpit/getCockpitData?${searchParams.toString()}`,
    });

    return {
      ...res,
      data: unwrapCockpitDatamartPayload(res.data),
    };
  };

  /**
   * GET /v2/pricing/cockpit/getCockpitConfig — loads cockpit configuration for create flow.
   * @param offerId Offer / case id
   */
  cockpitGetConfigs = async (offerId: string) => {
    if (!offerId) {
      return { status: false, data: [], message: 'Offer id is required' };
    }

    const searchParams = new URLSearchParams();
    searchParams.append('offerId', offerId);

    const res = await this.genericDatamartAPI({
      method: 'get',
      url: `/v2/pricing/cockpit/getCockpitConfig?${searchParams.toString()}`,
    });

    return {
      ...res,
      data: unwrapCockpitDatamartPayload(res.data),
    };
  };

  /**
   * Level-view reads (L2 / L3 / L2+L3 / L3+L2). GET-only; scoped by offerId.
   * @param {'l2'|'l3'|'l2l3'|'l3l2'} pathSegment - URL path after /profitability/
   * @param {string} offerId - Offer / case id
   */
  fetchProfitabilityByMode = async (
    pathSegment: 'l2' | 'l3' | 'l2l3' | 'l3l2',
    offerId: string,
  ) => {
    const { userId } = this.getState();
    try {
      const res = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_DATAMART}/v2/pricing/profitability/${pathSegment}`,
        params: { offerId },
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });
      return parseProfitabilityResult(res);
    } catch (error: any) {
      console.error(`Error in fetchProfitabilityByMode(${pathSegment}):`, error);
      return profitabilityErrorResult(error);
    }
  };

  /**
   * GET /v2/pricing/profitability/export?offerId= — downloads the profitability Excel file.
   * Filename prefers Content-Disposition, then the caller fallback:
   * `{opp_id}_{opp_name}_{offer_id}_{yyyy-mm-dd}.xlsx`.
   */
  exportProfitabilityExcel = async (
    offerId: string,
    options?: { fallbackFileName?: string },
  ) => {
    const fallbackMessage = 'Failed to export profitability';
    const error = new Error('Offer id is required');
    if (!offerId) {
      return {
        status: false,
        data: null as Blob | null,
        fileName: '',
        header: [],
        rows: [],
        payload: null,
        itemSource: null,
        metadata: null,
        hasLineItems: false,
        message: this.getErrorMessageFromError(error),
      };
    }

    const { userId } = this.getState();
    try {
      const res = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_DATAMART}/v2/pricing/profitability/export`,
        params: { offerId },
        responseType: 'blob',
        headers: {
          Accept: EXCEL_MIME,
          'x-user-id': userId,
        },
        _skipGlobalErrorDialog: true,
      } as any);

      const blob: Blob | null = res?.data instanceof Blob ? res.data : null;
      if (!blob) {
        return {
          status: false,
          data: null as Blob | null,
          fileName: '',
          message: fallbackMessage,
        };
      }

      if (blob.type && blob.type.includes('application/json')) {
        return {
          status: false,
          data: null as Blob | null,
          fileName: '',
          message: await parseBlobErrorMessage(blob, fallbackMessage),
        };
      }

      const disposition = getResponseHeader(res.headers, 'content-disposition');
      const fileName =
        parseContentDispositionFilename(disposition) ||
        options?.fallbackFileName ||
        buildProfitabilityExportFileName({ offerId });

      return {
        status: true,
        data: blob,
        fileName,
        message: '',
      };
    } catch (error: any) {
      console.error('Error in exportProfitabilityExcel:', error);
      const errData = error?.response?.data;
      let message = fallbackMessage;
      if (errData instanceof Blob) {
        message = await parseBlobErrorMessage(errData, error?.message || fallbackMessage);
      } else {
        message = messageFromExportPayload(errData, error?.message || fallbackMessage);
      }
      return {
        status: false,
        data: null as Blob | null,
        fileName: '',
        message,
      };
    }
  };

  /**
   * Load user table preferences for a specific page/component.
   * Always filters by page_or_component so Networks and Profitability rows stay isolated.
   */
  getTablePreferences = async (pageOrComponent: string) => {
    const { userId } = this.getState();
    try {
      // Backend appends `filter` into SQL WHERE as-is (same pattern as OOT comment).
      const query =
        `properties=ucp_id,page_or_component,preferences` +
        `&limit=1&offset=0` +
        `&filter=page_or_component="${pageOrComponent}"`;

      const res = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_DATAMART}/v2/table-preferences?${query}`,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });

      return {
        status: !!res?.data?.status,
        data: res?.data?.data || [],
      };
    } catch (error: any) {
      console.error('Error in getTablePreferences:', error);
      return {
        status: false,
        data: [],
        message: this.getErrorMessageFromError(error),
      };
    }
  }

  /**
   * Create table preferences for a page/component (POST /v2/table-preferences).
   */
  createTablePreferences = async (
    pageOrComponent: string,
    preferences: Record<string, unknown>,
  ) => {
    const { userId } = this.getState();
    try {
      const res = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_DATAMART}/v2/table-preferences`,
        data: {
          uid: userId,
          page_or_component: pageOrComponent,
          preferences,
        },
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });

      return {
        status: !!res?.data?.status,
        ucpId: res?.data?.ucp_id != null ? String(res.data.ucp_id) : '',
        message: res?.data?.msg || res?.data?.message || '',
      };
    } catch (error: any) {
      console.error('Error in createTablePreferences:', error);
      return {
        status: false,
        ucpId: '',
        message: this.getErrorMessageFromError(error),
      };
    }
  }

  /**
   * Update existing table preferences (PUT /v2/table-preferences/:ucpId).
   */
  updateTablePreferences = async (
    pageOrComponent: string,
    preferences: Record<string, unknown>,
    ucpId: string,
  ) => {
    const { userId } = this.getState();
    try {
      const res = await axios({
        method: 'put',
        url: `${process.env.REACT_APP_DATAMART}/v2/table-preferences/${ucpId}`,
        data: {
          uid: userId,
          page_or_component: pageOrComponent,
          preferences,
        },
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });

      return {
        status: !!res?.data?.status,
        ucpId: ucpId,
        message: res?.data?.msg || res?.data?.message || '',
      };
    } catch (error: any) {
      console.error('Error in updateTablePreferences:', error);
      return {
        status: false,
        ucpId,
        message: this.getErrorMessageFromError(error),
      };
    }
  }

}
const DATAMART_SERVICES = new DatamartServices();
export default DATAMART_SERVICES;
